'use client';

import React from 'react';
import { Button, Faq, MarketingSurface, useTalkToUs } from '@/components/marketing';
import { landingPageEvents } from '@/components/PostHogProvider';
import type { ContentFAQ } from '@/data/shared-types';
import { CitedText } from './CitedText';

interface ConceptFaqProps {
  faqs: ContentFAQ[];
  editorial?: boolean;
  /**
   * Which article template renders the band. It only keys the row ids
   * (`concept-faq-1`…, `comparison-faq-1`…) and the `talk-to-us` location
   * (`concept-faq`, `comparison-faq`), so the two routes stay separable in
   * PostHog; the section is the same on both.
   */
  kind: 'concept' | 'comparison';
}

/**
 * The FAQ band of the concept (`/learn/[slug]`) and comparison
 * (`/compare/[slug]`) articles: the shared `Faq` section in the homepage
 * design, wrapped in the page's one `MarketingSurface` so its tokens resolve
 * on a page that is otherwise Tailwind-styled. The surface carries
 * `.faq-section`, the speakable selector the concept `ArticleSchema` targets
 * by default. This file owns only what is the article's – the answers rendered
 * through `CitedText` so their inline citations keep resolving against the
 * page's `CitationProvider`, the CTA location and the `landing_faq_expanded`
 * event. Each page keeps its `faqs` array and feeds the same array to
 * `FAQPageSchema`, so the schema is untouched by the design.
 *
 * Row ids are positional: `ContentFAQ` items carry no id, and nothing in the
 * JSON-LD path reads one.
 */
export function ConceptFaq({ faqs, kind, editorial = false }: ConceptFaqProps): React.ReactElement {
  const onTalkToUs = useTalkToUs(`${kind}-faq`);

  const content = (
    <Faq
      id="faq"
      variant={editorial ? 'editorial' : 'split'}
      eyebrow="Common questions"
      heading="Frequently asked questions"
      card={{
        heading: 'Have a question we haven’t covered?',
        cta: (
          <Button aria-haspopup="dialog" onClick={onTalkToUs}>
            Talk to us
          </Button>
        ),
      }}
      items={faqs.map((faq, index) => ({
        id: `${kind}-faq-${index + 1}`,
        question: faq.question,
        answer: <CitedText>{faq.answer}</CitedText>,
      }))}
      onExpand={(id) => landingPageEvents.faqExpanded(id)}
    />
  );
  return editorial ? (
    <div className="faq-section">{content}</div>
  ) : (
    <MarketingSurface className="faq-section">{content}</MarketingSurface>
  );
}
